export type Tuning = {
  /** Downstream flow speed (m/s) in the middle of the river, outside the fast lane. */
  baseFlow: number;
  /** Extra speed (m/s) at the centre of the fast lane. */
  laneBoost: number;
  /** Gaussian width (m) of the fast lane. */
  laneWidth: number;
  /** How quickly the duck's velocity relaxes to the water's (per second). */
  grip: number;
  /** Sideways acceleration (m/s²) from a held nudge. */
  nudgeAccel: number;
  /** Tangential speed (m/s) at a whirlpool's core. */
  whirlSwirl: number;
  /** Inward pull (m/s) at a whirlpool's core. */
  whirlPull: number;
  /** Forward kick (m/s) of a paddle in the slowest water. */
  paddleKick: number;
  /** Extra kick (m/s) when paddling in the fastest water: surf the current. */
  paddleFastBonus: number;
  /** Seconds after a paddle during which the duck glides (reduced grip). */
  glideTime: number;
  /** Seconds for the slow automatic trickle to refill one paddle charge. */
  paddleRecharge: number;
  /** Charge earned per bread crumb collected (paddles are mostly earned). */
  crumbCharge: number;
  /** Little forward push (m/s) from each crumb, so eating them feels like speed. */
  crumbKick: number;
  /** Forward push (m/s) of a free flap, from a standstill. */
  flapKick: number;
  /**
   * Paddling while slower than this (m/s) is a free flap instead of spending
   * a charge: you can always work your way out of a stall, but only a saved
   * paddle surfs the fast lane. About the safe channel's speed.
   */
  flapCap: number;
  /** Share of speed kept after bouncing off a rock or log. */
  hitKeep: number;
  /** Share of speed kept when a hard hit spins you out. */
  spinKeep: number;
  /** Seconds a spin-out takes away your steering. */
  spinTime: number;
  /** How much a rock's wake slows the water right behind it (0..1). */
  rockWake: number;
  /** Bounce-back after a hit: forward pull (m/s²) back toward cruising speed... */
  recoverAccel: number;
  /** ...for this many seconds after the hit (and any spin) ends. */
  recoverTime: number;
  cameraLag: number;
  exposure: number;
  maxDpr: number;
};

export const DEFAULT_TUNING: Tuning = {
  baseFlow: 1.7,
  laneBoost: 3.3,
  laneWidth: 1.1,
  grip: 1.9,
  nudgeAccel: 6,
  whirlSwirl: 5,
  whirlPull: 2.6,
  paddleKick: 1.6,
  paddleFastBonus: 3.6,
  glideTime: 0.9,
  paddleRecharge: 10,
  crumbCharge: 0.12,
  crumbKick: 0.45,
  flapKick: 1.5,
  flapCap: 2.6,
  hitKeep: 0.7,
  spinKeep: 0.5,
  spinTime: 0.5,
  rockWake: 0.45,
  recoverAccel: 8,
  recoverTime: 1.2,
  cameraLag: 0.14,
  exposure: 1.05,
  maxDpr: 2,
};

export const DUCK_RADIUS = 0.45;
export const COUNTDOWN_SECONDS = 3;

export type Medal = { name: string; icon: string; time: number };
/**
 * Target times, fastest first. Bill races at about gold pace and Rosie at
 * about silver (see `npm run sim`); Sprout is slower than bronze.
 */
export const MEDALS: readonly Medal[] = [
  { name: 'Gold', icon: '🥇', time: 50 },
  { name: 'Silver', icon: '🥈', time: 57 },
  { name: 'Bronze', icon: '🥉', time: 64 },
];

export function medalFor(time: number | null): Medal | null {
  if (time === null) return null;
  return MEDALS.find((medal) => time <= medal.time) ?? null;
}
export const PADDLE_CHARGES = 3;
/** Seconds between flaps, so mashing can't beat a steady rhythm. */
export const FLAP_COOLDOWN = 0.4;
/** Crumbs eaten this close together (s) build a streak; every CRUMB_STREAK of them is a free paddle. */
export const CRUMB_STREAK_GAP = 1.4;
export const CRUMB_STREAK = 6;
/** Passing a rock this close (m, surface to surface) without touching is a close shave. */
export const CLOSE_SHAVE_GAP = 0.45;
